// AppShell — the ONE shell (docs/ONE_APP.md).
//
// There used to be three: FieldApp, DispatchApp and OfficeApp, each with its
// own route tree, its own tab bar and its own idea of what a person could
// reach. That is what left a crew member inside a five-route app whose only
// escape was /classic, and an admin on a phone inside Dispatch with four tabs
// and no route to the rest of their own product.
//
// This renders `apps/shared/navigation.js` and nothing else. Role decides what
// is in the menu; VIEWPORT decides how the menu looks. Neither decides which
// application you are in, because there is only one.
//
//   phone    a bottom tab bar of this role's first few destinations, plus a
//            More sheet carrying everything else they can reach. So the tab
//            bar is a crew member's whole day AND an admin still reaches all
//            of it, which the three-shell version could not do at once.
//   desktop  the sidebar, grouped, via ui/fw DesktopShell. The groups a role
//            lives in are open; the long tail is collapsed to its heading,
//            which carries whatever is waiting inside it. See navGroups.js —
//            an admin's sidebar was 26 equal rows and 1495px of content in an
//            844px column, so ten of their own screens were below the fold.
//
// Screens do not know which one they are in. They render their own content and
// let this decide the chrome — the same screen at both widths, not two screens.
//
// Two things are mounted here and nowhere else, because "once, above every
// route" is the whole point of having one shell:
//   <OfflineProvider>  the queue that makes a phone safe with no signal. It
//                      used to be mounted by FieldApp, so it covered five
//                      routes and no others.
//   <UpdateGate>       the service-worker handoff that gets a crew onto a new
//                      build without being told to reload.

import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DesktopShell, PhoneShell, Sheet, FwButton, Avatar, initialsOf, SectionLabel } from '../../ui/fw';
import { Icon, ThemeToggle } from '../../ui';
import { usePhoneLayout } from '../../ui/fw/usePhoneLayout';
import { useAuth, useSettings } from '../../App';
import { useRole } from '../../hooks/usePermission';
import UpdateGate from './UpdateGate';
import { ChromeProvider, useChrome } from './chrome';
import { OfflineProvider, OfflineBanner } from './offline';
import { useNavBadges } from './badges';
import { useLiveUpdates } from './liveUpdates';
import {
  navGroupsFor, phoneTabsFor, keyForPath, destinationsFor, barLabel, defaultOpenGroupsFor,
} from '../shared/navigation';
import {
  safeStorage, readTabPrefs, writeTabPrefs, clearTabPrefs, resolveTabs, resolveMore,
} from '../shared/tabPrefs';
import {
  readGroupPrefs, writeGroupPrefs, resolveOpenGroups, toggleGroup, groupBadge,
} from './navGroups';
import TabEditor from './TabEditor';
import '../field/field.css';
import '../dispatch/dispatch.css';
import '../office/office.css';
import './AppShell.css';

const MORE_KEY = '__more__';

/**
 * The sidebar takes its badge per item, so the counts are folded in here —
 * and, for a group that is CLOSED, rolled up onto its heading.
 *
 * That rollup is the same rule the phone's More tab already follows a few
 * lines below: something waiting behind a thing you cannot see is the same as
 * nothing waiting. Without it, closing Insight would hide seven unread
 * notifications and closing Dispatch would hide a submitted timesheet, which
 * would make this whole change a way to lose the only in-app signal there is.
 *
 * `open` is what tells DesktopShell a group is collapsible at all; a group
 * without one renders the way it always did.
 */
function withBadges(groups, badges, openGroups, lockedGroup) {
  return groups.map((g) => {
    const open = !openGroups || openGroups.has(g.label);
    return {
      ...g,
      open: openGroups ? open : undefined,
      // The group holding the screen you are on. resolveOpenGroups forces it
      // open last and nothing overrides that, so its toggle would persist a
      // preference and change nothing visible. Declared so the shell can say
      // so rather than render a control that does nothing.
      locked: !!(openGroups && lockedGroup && g.label === lockedGroup),
      badge: openGroups ? (groupBadge(g, badges, open) || undefined) : undefined,
      items: g.items.map((i) => ({ ...i, badge: badges[i.key] || undefined })),
    };
  });
}

function Frame({ children }) {
  const phone = usePhoneLayout();
  const role = useRole();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  // The branding an admin uploaded. Same access path as every other screen
  // that shows it — Login.js, admin/Settings.js, InvoicePrint.js — because a
  // second way to read one setting is a second way for it to go stale.
  const { settings } = useSettings();
  const [moreOpen, setMoreOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const chrome = useChrome();
  const badges = useNavBadges();
  // The shell's ONE socket subscription (docs/PARITY_2026-09-08.md §4). Here
  // rather than on a screen because the bell it refreshes lives here and the
  // push must arrive whatever screen is open — and because a second caller
  // would double every invalidation. See liveUpdates.js for why it is an
  // event and not another poll.
  useLiveUpdates();

  const activeKey = keyForPath(location.pathname);
  // The merged permission set, not the role label. A per-user grant widens the
  // menu through a destination's `needs` (shared/navigation.js) — which is how
  // a worker who holds jobs.create reaches the screen that creates one. `user`
  // can be null for a frame before /me hydrates; navigation.js treats a missing
  // set as "no extra rows", so the menu fills in rather than flickering wrong.
  const perms = user && user.permissions;
  const navOpts = useMemo(() => ({ permissions: perms }), [perms]);
  const groups = useMemo(() => navGroupsFor(role, navOpts), [role, navOpts]);
  const all = useMemo(() => destinationsFor(role, navOpts), [role, navOpts]);

  // ── the bar, as this person arranged it ───────────────────────────────────
  //
  // `prefs` is the saved order or null for "never chosen". It is state rather
  // than a read-through to storage so that saving re-renders the bar, and it
  // is seeded once because localStorage does not change underneath us.
  const storage = useMemo(() => safeStorage(), []);
  const [prefs, setPrefs] = useState(() => readTabPrefs(storage));
  const defaults = useMemo(() => phoneTabsFor(role, undefined, navOpts), [role, navOpts]);
  // resolveTabs filters against `all`, so a key saved under a role this person
  // no longer holds — or for a destination the app stopped offering — is
  // dropped rather than rendered as a tab that lands on a refusal.
  const tabs = useMemo(() => resolveTabs(all, defaults, prefs), [all, defaults, prefs]);
  const more = useMemo(() => resolveMore(all, tabs), [all, tabs]);

  const saveTabs = (keys) => {
    writeTabPrefs(storage, keys);
    setPrefs(keys);
    setEditOpen(false);
  };
  const resetTabs = () => {
    clearTabPrefs(storage);
    setPrefs(null);
    setEditOpen(false);
  };
  const here = all.find((d) => d.key === activeKey);

  // ── which sidebar groups are open ─────────────────────────────────────────
  //
  // Same three-part shape as the tab bar above, and for the same reason: the
  // defaults have to be a good guess because most people will never change
  // them, and changing them has to be possible because what a person opens
  // twenty times a day is a fact about their job, not their role.
  //
  // The third input is the one the bar does not have — WHERE THEY ARE. The
  // group holding the current screen is open whatever anyone chose, so a
  // collapsed group can never be somewhere you are standing and cannot see
  // out of. navGroups.js `resolveOpenGroups` applies that last, on purpose.
  const [groupPrefs, setGroupPrefs] = useState(() => readGroupPrefs(storage));
  const openDefaults = useMemo(() => defaultOpenGroupsFor(role, navOpts), [role, navOpts]);
  const openGroups = useMemo(
    () => resolveOpenGroups(groups, openDefaults, groupPrefs, here ? here.group : null),
    [groups, openDefaults, groupPrefs, here],
  );
  const onToggleGroup = (label) => {
    // Never for the group you are standing in: resolveOpenGroups forces that
    // one open regardless, so a preference written here would do nothing now
    // and would surprise somebody later, from a different screen.
    if (here && here.group === label) return;
    const next = toggleGroup(groupPrefs, label, openGroups.has(label));
    writeGroupPrefs(storage, next);
    setGroupPrefs(next);
  };

  const name = (user && user.name) || '';
  const brandLogo = (settings && settings.brand_logo) || '';
  const brandName = (settings && settings.brand_name) || '';

  // A detail screen names the thing you are looking at; everything else falls
  // back to the destination's own label, so most screens set nothing.
  const title = (chrome && chrome.title) || (here ? here.label : 'FieldWorks');
  const eyebrow = chrome ? chrome.eyebrow : (here && here.group ? here.group : '');

  const go = (path) => { setMoreOpen(false); navigate(path); };

  if (phone) {
    // A destination reached from More is not on the tab bar, so nothing there
    // should look selected — otherwise the bar claims you are somewhere you
    // are not. The More tab itself lights up instead.
    const onTabBar = tabs.some((t) => t.key === activeKey);
    return (
      <>
        <PhoneShell
          eyebrow={eyebrow}
          title={title}
          tabs={[
            ...tabs.map((t) => ({ key: t.key, label: barLabel(t), icon: t.icon, badge: badges[t.key] })),
            ...(more.length ? [{
              key: MORE_KEY,
              label: 'More',
              icon: 'hamburger',
              // Everything hidden behind More, added up. Without this a
              // submitted timesheet is invisible to anyone whose Approve tab
              // did not fit on the bar — which is most roles on a phone.
              badge: more.reduce((n, m) => n + (badges[m.key] || 0), 0),
            }] : []),
          ]}
          activeTab={onTabBar ? activeKey : (more.length ? MORE_KEY : activeKey)}
          onTabChange={(key) => {
            if (key === MORE_KEY) { setMoreOpen(true); return; }
            const d = tabs.find((t) => t.key === key);
            if (d) go(d.path);
          }}
          banner={<OfflineBanner />}
          headerRight={<ThemeToggle />}
          className="fw-oneapp"
        >
          <UpdateGate />
          {children}
        </PhoneShell>

        <Sheet open={moreOpen} title={name || 'Menu'} onClose={() => setMoreOpen(false)} className="fw-more">
          {groups.map((g) => {
            const rows = g.items.filter((i) => more.some((m) => m.key === i.key));
            if (!rows.length) return null;
            return (
              <div key={g.label || 'top'} className="fw-more__group">
                {g.label ? <SectionLabel>{g.label}</SectionLabel> : null}
                {rows.map((i) => (
                  <button
                    key={i.key}
                    type="button"
                    // Same `data-key` the tab bar carries. A More row was the
                    // one destination in this shell a browser check could only
                    // find by its LABEL — and the bar shows `short` while the
                    // sheet shows the full label, so comparing the two sets in
                    // a browser meant comparing two different spellings of the
                    // same four rows. scripts/ui-check.js `sidebar-reach-*`
                    // compares them by key because of this.
                    data-key={i.key}
                    className={`fw-more__row${i.key === activeKey ? ' is-active' : ''}`}
                    onClick={() => go(i.path)}
                  >
                    <Icon name={i.icon} />
                    <span>{i.label}</span>
                    {badges[i.key] ? <span className="fw-more__badge fw-num">{badges[i.key]}</span> : null}
                  </button>
                ))}
              </div>
            );
          })}
          <div className="fw-more__group">
            <FwButton
              variant="quiet"
              icon="settings"
              full
              data-action="edit-tabs"
              onClick={() => { setMoreOpen(false); setEditOpen(true); }}
            >
              Edit tabs
            </FwButton>
          </div>
          <FwButton variant="ghost" icon="logout" full onClick={() => { setMoreOpen(false); if (logout) logout(); }}>
            Log out
          </FwButton>
        </Sheet>

        <TabEditor
          open={editOpen}
          onClose={() => setEditOpen(false)}
          reachable={all}
          current={tabs}
          onSave={saveTabs}
          onReset={resetTabs}
          canPersist={!!storage}
        />
      </>
    );
  }

  // The uploaded logo replaces the wordmark, and the alt keeps BOTH names: the
  // company's, because that is what the picture shows, and the app's, because
  // otherwise a screen reader user loses the only thing on screen that says
  // which product this is. With nothing uploaded the wordmark stays.
  const logoAlt = brandName ? `${brandName} — FieldWorks` : 'FieldWorks';

  return (
    <DesktopShell
      brand="FieldWorks"
      logo={brandLogo}
      logoAlt={logoAlt}
      groups={withBadges(groups, badges, openGroups, here ? here.group : null)}
      onToggleGroup={onToggleGroup}
      activeKey={activeKey}
      onNavigate={(key) => {
        const d = all.find((x) => x.key === key);
        if (d) go(d.path);
      }}
      user={user ? { name, role } : undefined}
      onLogout={logout}
      topRight={
        <>
          <ThemeToggle />
          <button
            type="button"
            className="fw-oneapp__me"
            aria-label="Account menu"
            onClick={() => go('/me')}
          >
            <Avatar initials={initialsOf(name)} size="sm" tone="sched" />
          </button>
        </>
      }
      className="fw-oneapp"
      data-app="fieldworks"
    >
      <UpdateGate />
      <div className="of-page">
        <OfflineBanner />
        {children}
      </div>
    </DesktopShell>
  );
}

export default function AppShell({ children }) {
  return (
    <OfflineProvider>
      <ChromeProvider>
        <Frame>{children}</Frame>
      </ChromeProvider>
    </OfflineProvider>
  );
}
